"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import type { User } from "@supabase/supabase-js";

export type FavoriteItem = {
  id: string;
  itemName: string;
  pictureUrl: string;
  tagPrice: number | null;
  metal: string;
  stone: string;
  soldOut?: boolean;
  superLike?: boolean;
};

export type ReactionDirection = "like" | "dislike";

export const FAVORITES_STORAGE_KEY = "vault88-favorites";

/* ---------- localStorage helpers ---------- */

function readLocalFavorites(): FavoriteItem[] {
  if (typeof window === "undefined") return [];
  try {
    const stored = window.localStorage.getItem(FAVORITES_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as FavoriteItem[]) : [];
  } catch {
    return [];
  }
}

function writeLocalFavorites(items: FavoriteItem[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(items));
  window.dispatchEvent(
    new StorageEvent("storage", {
      key: FAVORITES_STORAGE_KEY,
      newValue: JSON.stringify(items),
    })
  );
}

/* ---------- Supabase helpers ---------- */

const supabase = typeof window !== "undefined" ? createClient() : null;

async function fetchSupabaseFavorites(): Promise<FavoriteItem[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from("favorites")
    .select("item_id, item_name, picture_url, tag_price, metal, stone, sold_out")
    .eq("direction", "like")
    .order("created_at", { ascending: false });
  if (error || !data) return [];
  return data.map((row) => ({
    id: row.item_id,
    itemName: row.item_name,
    pictureUrl: row.picture_url ?? "",
    tagPrice: row.tag_price != null ? Number(row.tag_price) : null,
    metal: row.metal ?? "",
    stone: row.stone ?? "",
    soldOut: row.sold_out ?? false,
  }));
}

async function upsertSupabaseFavorite(
  userId: string,
  item: FavoriteItem,
  direction: ReactionDirection
) {
  if (!supabase) return;
  await supabase.from("favorites").upsert(
    {
      user_id: userId,
      item_id: item.id,
      item_name: item.itemName,
      picture_url: item.pictureUrl,
      tag_price: item.tagPrice,
      metal: item.metal,
      stone: item.stone,
      sold_out: item.soldOut ?? false,
      direction,
      super_like: direction === "like" ? (item.superLike ?? false) : false,
    },
    { onConflict: "user_id,item_id" }
  );
}

async function deleteSupabaseFavorite(itemId: string) {
  if (!supabase) return;
  await supabase.from("favorites").delete().eq("item_id", itemId);
}

async function migrateLocalToSupabase(userId: string) {
  const local = readLocalFavorites();
  if (local.length === 0) return;
  await Promise.all(
    local.map((item) => upsertSupabaseFavorite(userId, item, "like"))
  );
  window.localStorage.removeItem(FAVORITES_STORAGE_KEY);
}

/* ---------- Standalone add/remove (used outside the hook) ---------- */

let _cachedUser: User | null = null;

async function resolveUser(): Promise<User | null> {
  if (_cachedUser) return _cachedUser;
  if (!supabase) return null;
  const { data } = await supabase.auth.getUser();
  _cachedUser = data.user;
  return _cachedUser;
}

// Listen for auth changes to keep cache fresh
if (supabase) {
  supabase.auth.onAuthStateChange((_event, session) => {
    _cachedUser = session?.user ?? null;
  });
}

/**
 * Persist a like or dislike for a catalog item.
 *
 * Likes: logged-in users write to Supabase favorites (direction='like');
 * logged-out users write to localStorage. localStorage likes migrate to
 * Supabase on login via migrateLocalToSupabase.
 *
 * Dislikes: logged-in only. Logged-out left-swipes stay ephemeral (no
 * localStorage, no migration). The onConflict (user_id, item_id) upsert
 * means swiping the same item the opposite way flips the single row's
 * direction in place rather than creating a duplicate.
 */
export async function addReaction(item: FavoriteItem, direction: ReactionDirection) {
  const user = await resolveUser();

  if (direction === "dislike") {
    if (!user) return; // logged-out dislikes are not persisted
    await upsertSupabaseFavorite(user.id, item, "dislike");
    // Notify the favorites hook so a previous like flipping to a dislike
    // disappears from the favorites page promptly.
    window.dispatchEvent(
      new StorageEvent("storage", { key: FAVORITES_STORAGE_KEY })
    );
    return;
  }

  // direction === "like"
  if (user) {
    await upsertSupabaseFavorite(user.id, item, "like");
  } else {
    const current = readLocalFavorites();
    if (current.some((existing) => existing.id === item.id)) return;
    writeLocalFavorites([...current, item]);
  }
  window.dispatchEvent(
    new StorageEvent("storage", { key: FAVORITES_STORAGE_KEY })
  );
}

export async function addFavorite(item: FavoriteItem) {
  return addReaction(item, "like");
}

/**
 * Persist a "super like" — a like with super_like=true. Logged-in users write
 * to Supabase; logged-out users store it in localStorage (carrying the flag),
 * and migrateLocalToSupabase upserts it as a super like on login. Super likes
 * still render on the favorites page because they remain direction='like'.
 */
export async function addSuperLike(item: FavoriteItem) {
  const user = await resolveUser();
  const flagged = { ...item, superLike: true };
  if (user) {
    await upsertSupabaseFavorite(user.id, flagged, "like");
  } else {
    const current = readLocalFavorites();
    const idx = current.findIndex((existing) => existing.id === item.id);
    if (idx >= 0) {
      const next = current.slice();
      next[idx] = flagged;
      writeLocalFavorites(next);
    } else {
      writeLocalFavorites([...current, flagged]);
    }
  }
  window.dispatchEvent(
    new StorageEvent("storage", { key: FAVORITES_STORAGE_KEY })
  );
}

export async function removeFavorite(id: string) {
  const user = await resolveUser();
  if (user) {
    await deleteSupabaseFavorite(id);
  } else {
    const current = readLocalFavorites();
    writeLocalFavorites(current.filter((item) => item.id !== id));
  }
  window.dispatchEvent(
    new StorageEvent("storage", { key: FAVORITES_STORAGE_KEY })
  );
}

/* ---------- Hook ---------- */

export function useFavorites() {
  const [items, setItems] = useState<FavoriteItem[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const migratedRef = useRef(false);

  // Auth listener
  useEffect(() => {
    if (!supabase) return;
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
      }
    );
    // Initial check
    supabase.auth.getUser().then(({ data }) => setUser(data.user));
    return () => subscription.unsubscribe();
  }, []);

  const refresh = useCallback(async () => {
    if (user) {
      const cloud = await fetchSupabaseFavorites();
      setItems(cloud);
    } else {
      setItems(readLocalFavorites());
    }
  }, [user]);

  // Migrate localStorage → Supabase on first login
  useEffect(() => {
    if (!user || migratedRef.current) return;
    migratedRef.current = true;
    migrateLocalToSupabase(user.id).then(() => refresh());
  }, [user, refresh]);

  // Initial load + polling + storage events
  useEffect(() => {
    refresh();

    const handleStorage = (event: StorageEvent) => {
      if (event.key === FAVORITES_STORAGE_KEY || event.key === null) refresh();
    };

    window.addEventListener("storage", handleStorage);
    const interval = window.setInterval(refresh, 3000);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.clearInterval(interval);
    };
  }, [refresh]);

  const removeItem = useCallback(
    async (id: string) => {
      // Optimistic update
      setItems((prev) => prev.filter((item) => item.id !== id));
      await removeFavorite(id);
    },
    []
  );

  const patchPictureUrls = useCallback(
    async (urlsById: Record<string, string>) => {
      const ids = Object.keys(urlsById);
      if (ids.length === 0) return;

      if (user && supabase) {
        const updates = ids.map((id) =>
          supabase!
            .from("favorites")
            .update({ picture_url: urlsById[id] })
            .eq("user_id", user.id)
            .eq("item_id", id)
        );
        await Promise.all(updates);
      } else {
        const current = readLocalFavorites();
        let changed = false;
        const next = current.map((item) => {
          const fresh = urlsById[item.id];
          if (fresh && fresh !== item.pictureUrl) {
            changed = true;
            return { ...item, pictureUrl: fresh };
          }
          return item;
        });
        if (changed) writeLocalFavorites(next);
      }

      setItems((prev) =>
        prev.map((item) => {
          const fresh = urlsById[item.id];
          return fresh && fresh !== item.pictureUrl ? { ...item, pictureUrl: fresh } : item;
        })
      );
    },
    [user]
  );

  return { items, refresh, removeItem, patchPictureUrls };
}
